import { useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Trophy, Grid3X3, Medal } from 'lucide-react';
import {
  AmericanFinalBracketSize,
  AmericanGroupFormat,
  FixtureType,
  OverflowGroupClassification,
  TournamentFormat,
} from '@/types/tournament.model';
import type { WizardStepProps, FixtureConfigSlice } from '../../../engine/types';
import { PADEL_DEFAULT_MAX_TEAMS, PADEL_DEFAULT_MIN_TEAMS } from './TeamConfigStep';
import {
  OptionCardsField,
  OVERFLOW_CLASSIFICATION_OPTIONS,
  AMERICAN_GROUP_FORMAT_OPTIONS,
  TournamentFormatField,
  LongStructureField,
  LongGroupConfigField,
  fixtureTypeForFormat,
  isFixtureTypeAllowed,
} from '../../../blocks/fixture-config';
import { TeamsAllowedField } from '../../../blocks/team-config';
import { cn } from '@/lib/utils';
import { FieldHelp } from '@/features/guided-help';

// Padel-specific defaults
const DEFAULT_TEAMS_PER_GROUP = 3;
const DEFAULT_CLASSIFIED_PER_GROUP = 2;

/**
 * Configuration for the Padel FixtureConfigStep component.
 */
export interface PadelFixtureConfigStepConfig {
  allowKnockoutsOnly?: boolean;
  teamsPerGroupOptions?: number[];
}

const DEFAULT_CONFIG: PadelFixtureConfigStepConfig = {
  allowKnockoutsOnly: true,
  teamsPerGroupOptions: [3, 4],
};

interface PadelFixtureConfigStepProps extends WizardStepProps<FixtureConfigSlice> {
  config?: PadelFixtureConfigStepConfig;
}

export function FixtureConfigStep({
  data,
  updateData,
  draft: _draft,
  config: userConfig,
}: PadelFixtureConfigStepProps) {
  void _draft;

  const config = { ...DEFAULT_CONFIG, ...userConfig };

  const isLong = data?.format === TournamentFormat.LONG;

  const handleFormatChange = useCallback(
    (format: TournamentFormat) => {
      const patch: Partial<FixtureConfigSlice> = { format };
      if (format === TournamentFormat.AMERICAN) {
        patch.americanFinalBracketSize = AmericanFinalBracketSize.NONE;
        patch.americanGroupFormat = AmericanGroupFormat.WINNER_LOSER;
        patch.teamsPerGroups = 4;
      } else {
        if (format !== TournamentFormat.SHORT) {
          patch.overflowGroupClassification = OverflowGroupClassification.STANDARD;
        }
        patch.americanFinalBracketSize = undefined;
        patch.americanGroupFormat = undefined;
        patch.teamsPerGroups = DEFAULT_TEAMS_PER_GROUP;
      }
      patch.fixtureType = fixtureTypeForFormat(format, data?.fixtureType);
      updateData(patch);
    },
    [updateData, data?.fixtureType],
  );

  const isAmerican = data?.format === TournamentFormat.AMERICAN;

  const showOverflowInGroupsCard =
    data?.format === TournamentFormat.SHORT &&
    data?.fixtureType === FixtureType.GROUPS_AND_KNOCKOUTS;

  const showAmericanGroupFormatInGroupsCard =
    isAmerican && data?.fixtureType === FixtureType.GROUPS_AND_KNOCKOUTS;

  // Initialize with defaults if no data
  useEffect(() => {
    if (!data) {
      updateData({
        fixtureType: FixtureType.GROUPS_AND_KNOCKOUTS,
        format: TournamentFormat.SHORT,
        teamsPerGroups: DEFAULT_TEAMS_PER_GROUP,
        classifiedPerGroups: DEFAULT_CLASSIFIED_PER_GROUP,
        overflowGroupClassification: OverflowGroupClassification.STANDARD,
        minTeamsAllowed: PADEL_DEFAULT_MIN_TEAMS,
        maxTeamsAllowed: PADEL_DEFAULT_MAX_TEAMS,
      });
    }
  }, [data, updateData]);

  // Coerce drafts without `format` to SHORT (drafts predating the LONG feature)
  useEffect(() => {
    if (!data) return;
    if (data.format === undefined) {
      updateData({ format: TournamentFormat.SHORT });
    }
  }, [data, data?.format, updateData]);

  return (
    <div className="space-y-6">
      {data && (
        <TournamentFormatField
          value={data.format ?? TournamentFormat.SHORT}
          onChange={handleFormatChange}
        />
      )}

      {/* Structure selector for LONG */}
      {isLong && data && (
        <LongStructureField
          value={data.fixtureType ?? FixtureType.GROUPS_AND_KNOCKOUTS}
          onChange={(fixtureType) => updateData({ fixtureType })}
        />
      )}

      {/* Group config for LONG with playoffs (zone size + qualified teams) */}
      {isLong && data && data.fixtureType === FixtureType.GROUPS_AND_KNOCKOUTS && (
        <LongGroupConfigField
          teamsPerGroups={data.teamsPerGroups ?? DEFAULT_TEAMS_PER_GROUP}
          classifiedPerGroups={data.classifiedPerGroups ?? DEFAULT_CLASSIFIED_PER_GROUP}
          onChange={(patch) => updateData(patch)}
        />
      )}

      {/* Fixture structure (SHORT/AMERICAN only) */}
      {!isLong && (
        <Card data-guide-id="wizard.fixture.fixtureType">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Trophy className="h-5 w-5" />
              Estructura del fixture
              <FieldHelp topicId="wizard.fixture.fixtureType" />
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4 sm:grid-cols-2">
              <FixtureTypeOption
                type={FixtureType.GROUPS_AND_KNOCKOUTS}
                title="Grupos + Eliminatorias"
                description="Fase de grupos seguida de eliminación directa"
                icon={<Grid3X3 className="h-6 w-6" />}
                isSelected={data?.fixtureType === FixtureType.GROUPS_AND_KNOCKOUTS}
                onSelect={() => updateData({ fixtureType: FixtureType.GROUPS_AND_KNOCKOUTS })}
              />
              <FixtureTypeOption
                type={FixtureType.KNOCKOUTS}
                title="Solo Eliminatorias"
                description="Eliminación directa desde el inicio"
                icon={<Medal className="h-6 w-6" />}
                isSelected={data?.fixtureType === FixtureType.KNOCKOUTS}
                onSelect={() =>
                  updateData({
                    fixtureType: FixtureType.KNOCKOUTS,
                    overflowGroupClassification: OverflowGroupClassification.STANDARD,
                  })
                }
                disabled={
                  !config.allowKnockoutsOnly ||
                  !isFixtureTypeAllowed(
                    data?.format ?? TournamentFormat.SHORT,
                    FixtureType.KNOCKOUTS,
                  )
                }
              />
            </div>
          </CardContent>
        </Card>
      )}

      {/* Group config: only for GROUPS_AND_KNOCKOUTS, never for LONG */}
      {!isLong && data?.fixtureType === FixtureType.GROUPS_AND_KNOCKOUTS && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Grid3X3 className="h-5 w-5" />
              Configuración de grupos
              <FieldHelp topicId="wizard.fixture.groupsFixed" />
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div
              className="grid gap-6 sm:grid-cols-2"
              data-guide-id="wizard.fixture.groupsFixed"
            >
              <div className="space-y-2">
                <Label htmlFor="teams-per-group">Equipos por grupo</Label>
                <Input
                  id="teams-per-group"
                  type="number"
                  min={2}
                  max={8}
                  value={data?.teamsPerGroups ?? DEFAULT_TEAMS_PER_GROUP}
                  onChange={(e) =>
                    updateData({
                      teamsPerGroups: parseInt(e.target.value) || DEFAULT_TEAMS_PER_GROUP,
                    })
                  }
                  disabled
                  className="bg-gray-50"
                />
                <p className="text-xs text-gray-500">
                  Valor fijo: {data?.teamsPerGroups ?? DEFAULT_TEAMS_PER_GROUP} equipos por grupo.
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="classified-per-group">Clasificados por grupo</Label>
                <Input
                  id="classified-per-group"
                  type="number"
                  min={1}
                  max={(data?.teamsPerGroups ?? DEFAULT_TEAMS_PER_GROUP) - 1}
                  value={data?.classifiedPerGroups ?? DEFAULT_CLASSIFIED_PER_GROUP}
                  onChange={(e) =>
                    updateData({
                      classifiedPerGroups: parseInt(e.target.value) || DEFAULT_CLASSIFIED_PER_GROUP,
                    })
                  }
                  disabled
                  className="bg-gray-50"
                />
                <p className="text-xs text-gray-500">
                  Valor fijo: {data?.classifiedPerGroups ?? DEFAULT_CLASSIFIED_PER_GROUP} de {data?.teamsPerGroups ?? DEFAULT_TEAMS_PER_GROUP} clasifican por grupo.
                </p>
              </div>
            </div>

            {showOverflowInGroupsCard && (
              <OptionCardsField
                label="Clasificados en grupos de 4"
                value={
                  data?.overflowGroupClassification ??
                  OverflowGroupClassification.STANDARD
                }
                onChange={(value) =>
                  updateData({ overflowGroupClassification: value })
                }
                options={OVERFLOW_CLASSIFICATION_OPTIONS}
                guideTopicId="wizard.fixture.overflowGroupClassification"
              />
            )}

            {showAmericanGroupFormatInGroupsCard && (
              <OptionCardsField
                label="Formato de partidos en el grupo"
                value={
                  data?.americanGroupFormat ?? AmericanGroupFormat.WINNER_LOSER
                }
                onChange={(value) => updateData({ americanGroupFormat: value })}
                options={AMERICAN_GROUP_FORMAT_OPTIONS}
                guideTopicId="wizard.fixture.americanGroupFormat"
              />
            )}
          </CardContent>
        </Card>
      )}

      <TeamsAllowedField
        minTeamsAllowed={data?.minTeamsAllowed ?? PADEL_DEFAULT_MIN_TEAMS}
        maxTeamsAllowed={data?.maxTeamsAllowed ?? PADEL_DEFAULT_MAX_TEAMS}
        onMinTeamsChange={(value) =>
          updateData({
            minTeamsAllowed: Math.max(2, value),
          })
        }
        onMaxTeamsChange={(value) =>
          updateData({
            maxTeamsAllowed: Math.max(2, value),
          })
        }
        minFloor={2}
      />
    </div>
  );
}

// ============================================================================
// FIXTURE TYPE OPTION
// ============================================================================

interface FixtureTypeOptionProps {
  type: FixtureType;
  title: string;
  description: string;
  icon: React.ReactNode;
  isSelected: boolean;
  onSelect: () => void;
  disabled?: boolean;
}

function FixtureTypeOption({
  type: _type,
  title,
  description,
  icon,
  isSelected,
  onSelect,
  disabled = false,
}: FixtureTypeOptionProps) {
  return (
    <button
      type="button"
      aria-pressed={isSelected}
      onClick={onSelect}
      disabled={disabled}
      className={cn(
        'flex flex-col items-center rounded-lg border-2 p-6 text-center transition-all',
        isSelected
          ? 'border-blue-500 bg-blue-50'
          : disabled
            ? 'cursor-not-allowed border-gray-100 bg-gray-50 opacity-50'
            : 'border-gray-200 hover:border-gray-300'
      )}
    >
      <div
        className={cn(
          'mb-3 rounded-full p-3',
          isSelected ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 text-gray-500'
        )}
      >
        {icon}
      </div>
      <h3
        className={cn(
          'font-semibold',
          isSelected ? 'text-blue-700' : 'text-gray-900'
        )}
      >
        {title}
      </h3>
      <p className="mt-1 text-sm text-gray-500">{description}</p>
      {disabled && (
        <span className="mt-2 text-xs text-gray-400">Próximamente</span>
      )}
    </button>
  );
}

export default FixtureConfigStep;
